// src/components/AutoUpdate.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CURRENT_BUILD } from '../version';

/**
 * Componente que verifica en segundo plano si existe una versión más reciente
 * del sitio en el servidor, forzando la actualización automática y sin fricción
 * para que el usuario siempre vea la última versión sin tener que recargar manualmente.
 */
export default function AutoUpdate() {
  const [isUpdating, setIsUpdating] = useState(false);
  const location = useLocation();
  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (checkingRef.current || isUpdating) return;

    try {
      checkingRef.current = true;
      // Parámetro _t para evitar cualquier caché HTTP del navegador
      const response = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (response.ok) {
        const serverBuild = await response.json().catch(() => null);

        if (serverBuild && serverBuild.buildTime && CURRENT_BUILD && CURRENT_BUILD.buildTime) {
          if (serverBuild.buildTime > CURRENT_BUILD.buildTime) {
            console.log(
              '[AutoUpdate] Nueva versión detectada en el servidor:',
              serverBuild.buildTime,
              'Actual:',
              CURRENT_BUILD.buildTime
            );

            // Prevenir loops infinitos de recarga
            const lastReload = sessionStorage.getItem('xf_last_auto_reload');
            const now = Date.now();
            if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
              sessionStorage.setItem('xf_last_auto_reload', now.toString());
              setIsUpdating(true);

              // Dar un breve momento para feedback visual y recargar limpiamente
              setTimeout(() => {
                window.location.reload();
              }, 900);
            }
          }
        }
      }
    } catch (err) {
      // Errores de red silenciosos para no interferir con la navegación
    } finally {
      checkingRef.current = false;
    }
  }, [isUpdating]);

  // 1. Verificar al montar el componente
  useEffect(() => {
    checkForUpdate();
  }, [checkForUpdate]);

  // 2. Verificar en cada cambio de ruta de React Router
  useEffect(() => {
    checkForUpdate();
  }, [location.pathname, checkForUpdate]);

  // 3. Verificar periódicamente (cada 25 segundos) y cuando la pestaña vuelve a tener foco
  useEffect(() => {
    const interval = setInterval(checkForUpdate, 25000);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };

    const onFocus = () => {
      checkForUpdate();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [checkForUpdate]);

  if (!isUpdating) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 99999,
        background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
        color: '#ffffff',
        padding: '12px 20px',
        borderRadius: '12px',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.6), 0 0 15px rgba(220, 38, 38, 0.4)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontWeight: '700',
        fontSize: '14px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        animation: 'fadeInUp 0.3s ease-out'
      }}
    >
      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      <span>¡Nueva versión de Xolo Fest detectada! Actualizando sitio...</span>
    </div>
  );
}
